import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { supabase } from '../supabaseClient'; // Use existing client

const QuestionPoolManager = ({ onNavigateToAdmin, onNavigateToDashboard }) => {
  const { user, userProfile } = useAuth();
  const [sections, setSections] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('create'); // 'create' or 'list'
  
  // Form state
  const [formData, setFormData] = useState({
    section_id: '',
    difficulty: 'easy',
    question_text: '',
    question_type: 'mcq',
    image_url: '',
    marks: 1,
    time_estimate_minutes: 2,
    options: [
      { text: '', is_correct: false },
      { text: '', is_correct: false },
      { text: '', is_correct: false },
      { text: '', is_correct: false }
    ]
  });
  
  // Image upload state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);

  // Check admin access
  useEffect(() => {
    if (userProfile && userProfile.role !== 'admin') {
      alert('Access denied. Admin privileges required.');
      if (onNavigateToDashboard) {
        onNavigateToDashboard();
      }
    }
  }, [userProfile, onNavigateToDashboard]);

  // Fetch sections on component mount
  useEffect(() => {
    fetchSections();
  }, [user?.id]);

  const fetchSections = async () => {
    if (!user?.id) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/sections?user_id=${user.id}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Sections API response:', data); // Debug log
        if (data.success) {
          if (data.sections && data.sections.length > 0) {
            setSections(data.sections);
            console.log('Sections loaded:', data.sections); // Debug log
          } else {
            console.log('No sections found, initializing...'); // Debug log
            // Initialize default sections if none exist
            const initResponse = await fetch(`${backendUrl}/api/admin/sections/init?user_id=${user.id}`, {
              method: 'POST'
            });
            
            if (initResponse.ok) {
              const initData = await initResponse.json();
              console.log('Sections init response:', initData); // Debug log
              if (initData.success) {
                setSections(initData.sections);
              }
            }
          }
        }
      } else {
        console.error('Failed to fetch sections:', response.status); // Debug log
      }
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const fetchQuestions = async (sectionId = null) => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const url = sectionId 
        ? `${backendUrl}/api/admin/questions/list?user_id=${user.id}&section_id=${sectionId}`
        : `${backendUrl}/api/admin/questions/list?user_id=${user.id}`;
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setQuestions(data.questions);
        }
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return null;

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `question_images/${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('question-images')
        .upload(fileName, file);

      if (error) {
        console.error('Upload error:', error);
        alert('Image upload failed. Please try again.');
        return null;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('question-images')
        .getPublicUrl(fileName);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Image upload failed. Please try again.');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...formData.options];
    newOptions[index][field] = value;
    
    // If marking this as correct, unmark others
    if (field === 'is_correct' && value) {
      newOptions.forEach((option, i) => {
        if (i !== index) option.is_correct = false;
      });
    }
    
    setFormData({ ...formData, options: newOptions });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.id) return;

    // Validation
    if (!formData.section_id || !formData.question_text.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.question_type === 'mcq') {
      const hasCorrectAnswer = formData.options.some(opt => opt.is_correct);
      const allOptionsFilled = formData.options.every(opt => opt.text.trim());
      
      if (!hasCorrectAnswer) {
        alert('Please mark one option as correct');
        return;
      }
      
      if (!allOptionsFilled) {
        alert('Please fill in all 4 options');
        return;
      }
    }

    setLoading(true);
    try {
      // Upload image if present
      let imageUrl = formData.image_url;
      if (imageFile) {
        imageUrl = await handleImageUpload(imageFile);
        if (!imageUrl) {
          setLoading(false);
          return;
        }
      }

      // Prepare submission data
      const submissionData = {
        ...formData,
        image_url: imageUrl,
        options: formData.question_type === 'mcq' ? formData.options : null
      };

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/questions/create?user_id=${user.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert('Question created successfully!');
          
          // Reset form
          setFormData({
            section_id: '',
            difficulty: 'easy',
            question_text: '',
            question_type: 'mcq',
            image_url: '',
            marks: 1,
            time_estimate_minutes: 2,
            options: [
              { text: '', is_correct: false },
              { text: '', is_correct: false },
              { text: '', is_correct: false },
              { text: '', is_correct: false }
            ]
          });
          setImageFile(null);
          setImagePreview('');
          
          // Switch to list view to see the created question
          setActiveView('list');
          fetchQuestions();
        }
      } else {
        const error = await response.text();
        alert(`Failed to create question: ${error}`);
      }
    } catch (error) {
      console.error('Error creating question:', error);
      alert('Error creating question. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">📝</span>
              <h1 className="text-2xl font-bold text-gray-900">Question Pool Manager</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={onNavigateToAdmin}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Back to Admin Panel
              </button>
              <button
                onClick={onNavigateToDashboard}
                className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveView('create')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeView === 'create'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Create Question
            </button>
            <button
              onClick={() => {
                setActiveView('list');
                fetchQuestions();
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeView === 'list'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Manage Questions
            </button>
          </nav>
        </div>

        {/* Create Question Form */}
        {activeView === 'create' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Create New Question</h3>
            
            {/* Debug info */}
            <div className="mb-4 p-3 bg-gray-100 rounded-lg">
              <p className="text-sm text-gray-600">
                Debug: Sections loaded: {sections.length} | User ID: {user?.id}
              </p>
              {sections.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-gray-500">Sections:</p>
                  <ul className="text-xs text-gray-500">
                    {sections.map(section => (
                      <li key={section.id}>{section.name} (ID: {section.id})</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Section Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Section *
                </label>
                <select
                  value={formData.section_id}
                  onChange={(e) => setFormData({ ...formData, section_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select a section</option>
                  {sections.map(section => (
                    <option key={section.id} value={section.id}>
                      {section.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Difficulty */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Difficulty *
                  </label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>

                {/* Question Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Question Type *
                  </label>
                  <select
                    value={formData.question_type}
                    onChange={(e) => setFormData({ ...formData, question_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="mcq">Multiple Choice</option>
                    <option value="writing">Writing</option>
                  </select>
                </div>

                {/* Marks */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Marks
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.marks}
                    onChange={(e) => setFormData({ ...formData, marks: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Question Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Question Text *
                </label>
                <textarea
                  value={formData.question_text}
                  onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter the question text..."
                  required
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Question Image (Optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {imagePreview && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 mb-2">Image Preview:</p>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="max-w-xs h-auto border border-gray-300 rounded-lg"
                    />
                  </div>
                )}
              </div>

              {/* MCQ Options */}
              {formData.question_type === 'mcq' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Answer Options *
                  </label>
                  <div className="space-y-3">
                    {formData.options.map((option, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <span className="text-sm font-medium text-gray-500 w-8">
                          {String.fromCharCode(65 + index)}.
                        </span>
                        <input
                          type="text"
                          value={option.text}
                          onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder={`Option ${String.fromCharCode(65 + index)}`}
                          required
                        />
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="correct_answer"
                            checked={option.is_correct}
                            onChange={(e) => handleOptionChange(index, 'is_correct', e.target.checked)}
                            className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-600">Correct</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading || uploading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  {loading ? 'Creating...' : uploading ? 'Uploading...' : 'Create Question'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Question List */}
        {activeView === 'list' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Question Pool</h3>
            </div>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading questions...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Question
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Section
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Difficulty
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Image
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {questions.map((question) => (
                      <tr key={question.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="max-w-xs truncate">
                            {question.question_text}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {sections.find(s => s.id === question.section_id)?.section_name || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            question.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                            question.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {question.difficulty}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {question.question_type.toUpperCase()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {question.image_url ? '✅' : '❌'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {questions.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No questions found. Create your first question!
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionPoolManager;